import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { BillPayService } from '../../services/bill-pay.service';
import { BrandService } from '../../core/services/brand.service';
import { TokenStorageService } from '../../core/services/token-storage.service';
import { IBillPayResponse, IClientPortalDetailsResponse } from '../../models/billing.model';
import { CreditCardTypeEnum } from '../../models/credit-card-type-enum.model';
import { STATES } from '../../models/states.model';

@Component({
  selector: 'app-bill-pay',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DatePipe, ReactiveFormsModule, NgxMaskDirective],
  providers: [provideNgxMask()],
  templateUrl: './bill-pay.component.html',
  styleUrl: './bill-pay.component.scss',
})
export class BillPayComponent implements OnInit {
  protected brandService = inject(BrandService);
  private billPayService = inject(BillPayService);
  private tokenStorage = inject(TokenStorageService);
  private router = inject(Router);
  private fb = inject(FormBuilder);

  details: IClientPortalDetailsResponse | null = null;
  loading = true;
  submitting = false;
  errorMessage = '';
  successMessage = '';
  paymentResult: IBillPayResponse | null = null;

  detectedCardType: CreditCardTypeEnum | null = null;

  paymentForm: FormGroup = this.fb.group({
    ccFirstName: ['', Validators.required],
    ccLastName: ['', Validators.required],
    ccAddress1: ['', Validators.required],
    ccAddress2: [''],
    ccCity: ['', Validators.required],
    ccState: ['', Validators.required],
    ccZip: ['', Validators.required],
    ccNumber: ['', [Validators.required, BillPayComponent.fifteenDigitMustBeAmex]],
    ccExp: ['', Validators.required],
    cvv: ['', Validators.required],
  });

  states = STATES;

  cardMap: Record<string, { src: string; tooltip: string; alt: string }> = {
    [CreditCardTypeEnum.visa]: { src: 'assets/images/cards/visa.svg', tooltip: 'Visa', alt: 'Visa' },
    [CreditCardTypeEnum.mc]: { src: 'assets/images/cards/mastercard.svg', tooltip: 'Mastercard', alt: 'Mastercard' },
    [CreditCardTypeEnum.amex]: { src: 'assets/images/cards/american-express.svg', tooltip: 'American Express', alt: 'American Express' },
    [CreditCardTypeEnum.discover]: { src: 'assets/images/cards/discover-card.svg', tooltip: 'Discover', alt: 'Discover' },
  };

  get cardInfo() {
    return this.cardMap[this.detectedCardType ?? ''] ?? null;
  }

  get ccNumberMask(): string {
    return '0000-0000-0000-0000||0000-000000-00000';
  }

  private static fifteenDigitMustBeAmex(control: AbstractControl): ValidationErrors | null {
    const digits = (control.value || '').replace(/\D/g, '');
    if (digits.length === 15 && !/^3[47]/.test(digits)) {
      return { invalidAmex: true };
    }
    return null;
  }

  ngOnInit(): void {
    this.loadDetails();
    this.paymentForm.get('ccNumber')?.valueChanges.subscribe(value => {
      this.detectedCardType = this.getCardType(value || '');
    });
  }

  private getCardType(ccNumber: string): CreditCardTypeEnum | null {
    const digits = ccNumber.replace(/\D/g, '');
    const patterns: Record<string, RegExp> = {
      [CreditCardTypeEnum.visa]: /^4\d{12}(\d{3})?(\d{3})?$/,
      [CreditCardTypeEnum.mc]: /^(5[1-5]\d{2}|2[2-7]\d{2})\d{12}$/,
      [CreditCardTypeEnum.amex]: /^3[47]\d{13}$/,
      [CreditCardTypeEnum.discover]: /^(6011\d{12}|65\d{14}|64[4-9]\d{13}|622(12[6-9]\d{10}|1[3-9]\d{11}|[2-8]\d{12}|9[01]\d{11}|92[0-5]\d{10}))$/,
    };
    for (const [brand, regex] of Object.entries(patterns)) {
      if (regex.test(digits)) {
        return brand as CreditCardTypeEnum;
      }
    }
    return null;
  }

  private loadDetails(): void {
    this.billPayService.getDetails().subscribe({
      next: data => {
        this.details = data;
        this.loading = false;
      },
      error: err => {
        if (err.status === 401) {
          this.tokenStorage.clearToken();
          this.router.navigate(['/login']);
        } else {
          this.errorMessage = 'Unable to load billing details. Please try again later.';
          this.loading = false;
        }
      },
    });
  }

  onSubmit(): void {
    if (this.paymentForm.invalid) {
      this.paymentForm.markAllAsTouched();
      return;
    }

    if (!this.detectedCardType) {
      this.errorMessage = 'Unable to determine card type. Please check the card number.';
      return;
    }

    this.submitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.paymentForm.value;
    const request = {
      ccType: this.detectedCardType,
      ccNumber: formValue.ccNumber.replace(/\D/g, ''),
      ccExp: formValue.ccExp,
      cvv: formValue.cvv,
      ccFirstName: formValue.ccFirstName,
      ccLastName: formValue.ccLastName,
      ccAddress1: formValue.ccAddress1,
      ccAddress2: formValue.ccAddress2 || '',
      ccCity: formValue.ccCity,
      ccState: formValue.ccState,
      ccZip: formValue.ccZip,
      ccCountry: 'US',
    };

    this.billPayService.submitPayment(request).subscribe({
      next: result => {
        this.submitting = false;
        this.paymentResult = result;
        if (result.success) {
          this.successMessage = 'Thank you for your payment! A receipt has been sent to your email address.';
        } else {
          this.errorMessage = result.message || 'Payment failed. Please check your information and try again.';
        }
      },
      error: err => {
        this.submitting = false;
        if (err.status === 401) {
          this.tokenStorage.clearToken();
          this.router.navigate(['/login']);
        } else {
          this.errorMessage = 'An error occurred while processing your payment. Please try again.';
        }
      },
    });
  }

  isInvalid(field: string): boolean {
    const control = this.paymentForm.get(field);
    return !!(control?.touched && control?.invalid);
  }
}
